/**
 * own-read-cache — helpers for the 60-second negative-cache that prevents an
 * own group message with __NO_BACKUP__ from hitting the server on every render.
 *
 * Mirrors the __NO_FAILSAFE__ pattern used by tryPeerPlaintextFailsafe but
 * scoped strictly to own-sender group messages and with a shorter TTL (60s)
 * so that a delayed backup write can still surface within one minute.
 */

export const OWN_MISS_PREFIX = "__OWN_MISS__:";
export const OWN_MISS_TTL_MS = 60 * 1000; // 60 seconds

/**
 * Returns true when `marker` is a valid, non-expired OWN_MISS sentinel,
 * meaning the own-fallback server read already failed and we should skip
 * the round-trip on this render.
 *
 * @param marker   Value retrieved from IDB / in-memory cache for the message.
 * @param now      Current timestamp in milliseconds (injectable for testing).
 */
export function shouldSkipOwnReread(marker: string | null, now: number): boolean {
  if (!marker || !marker.startsWith(OWN_MISS_PREFIX)) return false;
  const expiry = parseInt(marker.slice(OWN_MISS_PREFIX.length), 10);
  if (isNaN(expiry)) return false;
  return now < expiry;
}

/**
 * Creates a fresh OWN_MISS sentinel value that expires `ttlMs` milliseconds
 * from `now`.  Write this value into the plaintext IDB cache (and/or an
 * in-memory map) so subsequent renders short-circuit without a server call.
 *
 * @param now    Current timestamp in milliseconds (injectable for testing).
 * @param ttlMs  How long the negative cache should live (default: 60 s).
 */
export function makeOwnMissMarker(now: number, ttlMs: number = OWN_MISS_TTL_MS): string {
  return `${OWN_MISS_PREFIX}${now + ttlMs}`;
}
